import { useMemo } from 'react'
import { Collapsible } from '~/@/components/ui/collapsible'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/@/components/ui/tabs'
import { cn } from '~/@/lib/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { CustomFilterPanel } from '~/components/shared/Table/CustomFilterPanel'
import { CustomSortPanel } from '~/components/shared/Table/CustomSortPanel'
import { LookupRefinement } from './useLookupRefinement'

interface LookupRefinementPanelProps {
  refinement: LookupRefinement
  className?: string
}

/**
 * Collapsible "Filter & sort" panel for a lookup picker: ad-hoc filters and sorts the user layers
 * on top of the configured lookup filter expression. Shared by the single-cell popup
 * (LookupRowSearch) and the bulk "Insert Multiple Rows" dialog (BulkSelectLookupModal).
 *
 * Renders nothing until the supporting table data is loaded (`refinement.isReady`).
 */
export const LookupRefinementPanel = ({ refinement, className }: LookupRefinementPanelProps) => {
  const { t } = useTypedTranslation()
  const {
    detailedTable,
    activeUsers,
    filterPanelColumns,
    filterModel,
    setFilterModel,
    sortModel,
    setSortModel,
    filterCount,
    sortCount,
    isReady,
  } = refinement

  const refinementSummary = useMemo(() => {
    if (filterCount === 0 && sortCount === 0) {
      return ''
    }
    const parts: string[] = []
    if (filterCount > 0) {
      parts.push(t('form.category.bulk_select_refine_filters_count', { count: filterCount }))
    }
    if (sortCount > 0) {
      parts.push(t('form.category.bulk_select_refine_sorts_count', { count: sortCount }))
    }
    return parts.join(' · ')
  }, [filterCount, sortCount, t])

  if (!isReady || !detailedTable) {
    return null
  }

  return (
    <div
      className={cn('min-w-0 shrink-0 overflow-hidden rounded-md border border-gray-4', className)}
    >
      <Collapsible
        defaultOpen={filterCount > 0 || sortCount > 0}
        keepMounted
        lazy
        title={
          <div className="flex w-full min-w-0 items-center justify-between gap-2 px-2">
            <div className="text-sm font-medium text-gray-12">
              {t('form.category.bulk_select_filter_and_sort')}
            </div>
            {refinementSummary ? (
              <div className="max-w-[55%] truncate text-xs text-gray-9">{refinementSummary}</div>
            ) : null}
          </div>
        }
        titleClassName="w-full px-3 py-2.5"
      >
        <div className="max-h-[min(40vh,320px)] overflow-x-auto border-t border-gray-4">
          <Tabs className="w-full min-w-0" defaultValue="filter">
            <TabsList className="px-3 pt-2">
              <TabsTrigger value="filter">{t('table_tabs.filters')}</TabsTrigger>
              <TabsTrigger value="sort">{t('table_tabs.sort')}</TabsTrigger>
            </TabsList>
            <TabsContent className="mt-0 max-h-[min(40vh,320px)] overflow-y-auto" value="filter">
              <CustomFilterPanel
                activeUsers={activeUsers}
                columns={filterPanelColumns}
                filterModel={filterModel}
                lookupRefinementMode
                onFilterModelChange={setFilterModel}
                pendingFilterItem={null}
                readOnly={false}
                table={detailedTable}
                tableId={detailedTable.id}
              />
            </TabsContent>
            <TabsContent className="mt-0 max-h-[min(40vh,320px)] overflow-y-auto" value="sort">
              <CustomSortPanel
                columns={filterPanelColumns}
                lookupRefinementMode
                onSortModelChange={setSortModel}
                pendingSortDirection={null}
                pendingSortField={null}
                readOnly={false}
                sortModel={sortModel}
                tableId={detailedTable.id}
              />
            </TabsContent>
          </Tabs>
        </div>
      </Collapsible>
    </div>
  )
}
